import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Phone } from "lucide-react";
import { HERO_IMG, PHONE_DISPLAY, PHONE_TEL, btnInk, btnLine } from "./data";
import { MaskLine, Star } from "./motion";
import { scrollTo } from "./scroll";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden border-b-2 border-ink bg-paper pt-16"
    >
      <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-10 px-4 pb-16 pt-10 sm:px-6 lg:grid-cols-12 lg:gap-6 lg:pb-24 lg:pt-16">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <MaskLine>
            <span className="inline-flex items-center gap-3 font-mono2 text-[11px] font-bold uppercase tracking-[0.35em] text-ink/70">
              <Star size={14} /> St Kilda, Melbourne <Star size={14} /> Est. 2012
            </span>
          </MaskLine>

          <h1 className="mt-6 font-display uppercase leading-[0.82] tracking-tight">
            <MaskLine delay={0.1} className="text-[19vw] sm:text-[15vw] lg:text-[9.5rem]">
              <span>Oliver&rsquo;s</span>
            </MaskLine>
            <MaskLine delay={0.22} className="text-[19vw] sm:text-[15vw] lg:text-[9.5rem]">
              <span className="text-polered">Barber</span>
            </MaskLine>
            <MaskLine delay={0.34} className="text-[19vw] sm:text-[15vw] lg:text-[9.5rem]">
              <span className="text-rust">Shop</span>
            </MaskLine>
          </h1>

          <div className="mt-6 font-display uppercase leading-[0.9]">
            <MaskLine delay={0.4} className="text-[10vw] sm:text-6xl lg:text-7xl">
              <span>Good cuts.</span>
            </MaskLine>
            <MaskLine delay={0.5} className="text-[10vw] sm:text-6xl lg:text-7xl">
              <span className="font-hand normal-case tracking-normal text-polered -rotate-2 inline-block">
                No BS.
              </span>
            </MaskLine>
          </div>

          <MaskLine delay={0.65}>
            <p
              data-testid="hero-copy"
              className="mt-6 max-w-md text-base font-medium text-ink/80 sm:text-lg"
            >
              Fades, classic cuts and good old-fashioned barbering in the heart
              of St Kilda.
            </p>
          </MaskLine>

          <MaskLine delay={0.8}>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#walkin"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo("#walkin");
                }}
                data-testid="hero-walkin-button"
                className={btnInk}
              >
                Walk ins welcome <ArrowDownRight size={20} />
              </a>
              <a href={PHONE_TEL} data-testid="hero-call-button" className={btnLine}>
                <Phone size={18} /> Call {PHONE_DISPLAY}
              </a>
            </div>
          </MaskLine>

          <MaskLine delay={0.95}>
            <div className="mt-6">
              <span
                data-testid="hero-free-drink-badge"
                className="inline-flex -rotate-2 items-center gap-2 border-2 border-ink bg-paper px-4 py-2 font-hand text-lg text-ink shadow-hard-sm"
              >
                <span className="text-polered" aria-hidden="true">★</span> Free drink with every cut or shave
              </span>
            </div>
          </MaskLine>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 3 }}
            animate={{ opacity: 1, y: 0, rotate: 1.5 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div
              data-testid="hero-barber-pole"
              className="barber-pole barber-pole-lg absolute -left-4 top-12 z-20 hidden -rotate-2 sm:-left-7 sm:inline-flex"
              aria-hidden="true"
            >
              <span className="barber-pole-cap" />
              <span className="barber-pole-glass" />
              <span className="barber-pole-cap" />
            </div>
            <motion.div
              style={{ y: imgY }}
              className="relative overflow-hidden border-2 border-ink shadow-hard"
            >
              <img
                src={HERO_IMG}
                alt="Barber's hands fading hair with electric clippers"
                data-testid="hero-image"
                className="grit h-[420px] w-full object-cover sm:h-[520px] lg:h-[620px]"
                loading="eager"
              />
              <div className="absolute inset-0 bg-rust/10 mix-blend-multiply" />
            </motion.div>

            <div
              data-testid="hero-stamp"
              className="absolute -bottom-5 -left-3 -rotate-6 border-2 border-ink bg-paper px-4 py-2 shadow-hard-sm sm:-left-6"
            >
              <span className="font-hand text-xl text-polered">EST. 2012</span>
            </div>

            <div className="absolute -top-4 right-6 rotate-6 border-2 border-ink bg-rust px-3 py-1 shadow-hard-sm">
              <span className="font-mono2 text-[10px] font-bold uppercase tracking-[0.25em] text-paper">
                Haircuts / Fades / Beards
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-8 right-6 hidden xl:block">
        <span className="font-mono2 text-[10px] uppercase tracking-[0.5em] text-ink/50 [writing-mode:vertical-rl]">
          St Kilda VIC — 3182
        </span>
      </div>
    </section>
  );
};

export default Hero;
